import React, { useState } from "react";
import type { HubSpotProduct, HubSpotField } from "../../types/configurator";
import { SearchIcon, ChevronIcon } from "../ui/Icons";
import { formatPrice } from "../../utils";
import "./CatalogSidebar.css";

interface Props {
  categories: Record<string, HubSpotProduct[]>;
  onSearch: (query: string) => Record<string, HubSpotProduct[]>;
  loading?: boolean;
  fields: HubSpotField[];
  fieldsLoading?: boolean;
}

export const CatalogSidebar: React.FC<Props> = ({
  categories,
  onSearch,
  loading,
  fields,
  fieldsLoading,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'products' | 'fields'>('products');
  const [selectedField, setSelectedField] = useState<string>(() => fields[0]?.name ?? "");

  // Keep selectedField in sync when fields load
  React.useEffect(() => {
    if (fields.length > 0 && !selectedField) {
      setSelectedField(fields[0].name);
    }
  }, [fields, selectedField]);

  const displayCategories = searchQuery.trim()
    ? onSearch(searchQuery)
    : categories;

  const toggleCategory = (cat: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      return next;
    });
  };

  const handleDragStart = (e: React.DragEvent, productId: string) => {
    e.dataTransfer.setData("productId", productId);
    e.dataTransfer.setData("source", "catalog");
    e.dataTransfer.effectAllowed = "copy";
  };

  const handleFieldValueDragStart = (
    e: React.DragEvent,
    field: string,
    value: string,
    label: string
  ) => {
    e.dataTransfer.setData("source", "field-value");
    e.dataTransfer.setData("fieldName", field);
    e.dataTransfer.setData("fieldValue", value);
    e.dataTransfer.setData("fieldLabel", label);
    e.dataTransfer.effectAllowed = "copy";
  };

  const currentField = fields.find((f) => f.name === selectedField);

  return (
    <aside className="catalog">
      <div className="catalog-header">
        <div className="catalog-logo">
          <img src="/logo.png" alt="Happy Timber" />
        </div>
        <h2>
          <div className="dot" />
          HubSpot Catalog
        </h2>

        <div className="catalog-tabs">
          <button
            className={`catalog-tab${activeTab === 'products' ? ' active' : ''}`}
            onClick={() => setActiveTab('products')}
          >
            Products
          </button>
          <button
            className={`catalog-tab${activeTab === 'fields' ? ' active' : ''}`}
            onClick={() => setActiveTab('fields')}
          >
            Filters
          </button>
        </div>

        {activeTab === 'products' && (
          <div className="search-box">
            <span className="search-icon">
              <SearchIcon />
            </span>
            <input
              type="text"
              placeholder="Search by name or SKU..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        )}
      </div>

      <div className="catalog-list">
        {activeTab === 'products' && (
          <>
            {loading && <div className="catalog-loading">Loading products...</div>}
            {Object.entries(displayCategories).map(([cat, products]) => (
              <div key={cat} className="catalog-category">
                <button
                  className="category-toggle"
                  onClick={() => toggleCategory(cat)}
                >
                  <ChevronIcon open={!collapsed.has(cat)} />
                  {cat}
                  <span className="count">{products.length}</span>
                </button>
                {!collapsed.has(cat) &&
                  products.map((p) => (
                    <div
                      key={p.id}
                      className="catalog-item"
                      draggable
                      onDragStart={(e) => handleDragStart(e, p.id)}
                    >
                      <div className="product-icon">
                        {p.imageUrl
                          ? <img src={p.imageUrl} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
                          : p.sku.split("-")[0].charAt(0)}
                      </div>
                      <div className="product-info">
                        <div className="product-name">{p.name}</div>
                        <div className="product-meta">
                          {formatPrice(p.price)} · {p.sku}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            ))}
          </>
        )}

        {activeTab === 'fields' && (
          <>
            {fieldsLoading && <div className="catalog-loading">Loading fields...</div>}
            {!fieldsLoading && fields.length === 0 && (
              <div className="catalog-loading">No enumeration fields found.</div>
            )}
            {!fieldsLoading && fields.length > 0 && (
              <>
                <div className="field-picker-wrap">
                  <select
                    className="field-picker"
                    value={selectedField}
                    onChange={(e) => setSelectedField(e.target.value)}
                  >
                    {[...fields].sort((a, b) => a.label.localeCompare(b.label)).map((f) => (
                      <option key={f.name} value={f.name}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                </div>
                {currentField && [...currentField.options].sort((a, b) => a.label.localeCompare(b.label)).map((opt) => (
                  <div
                    key={opt.value}
                    className="catalog-item"
                    draggable
                    onDragStart={(e) =>
                      handleFieldValueDragStart(e, currentField.name, opt.value, opt.label)
                    }
                  >
                    <div className="product-icon field-value-icon">
                      {opt.label.charAt(0).toUpperCase()}
                    </div>
                    <div className="product-info">
                      <div className="product-name">{opt.label}</div>
                      <div className="product-meta">{opt.value}</div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </div>
    </aside>
  );
};
